import {Injectable} from '@angular/core';
import {deepEqual, isNonNull} from '@emma-labs/shared/angular';
import {Profile, PublicProfile, Result, ResultStatus} from '@emma-labs/shared/typescript';
import {BehaviorSubject, distinctUntilChanged, filter, Observable, of, Subject, switchMap, tap} from 'rxjs';
import {catchError, map, mergeMap, throttleTime} from 'rxjs/operators';
import {PasswordUpdateRequest} from '../../ui/profile-page/password/password-update-request';
import {AgentApiService} from '../agent-api.service';
import {AuthService} from '../auth.service';

@Injectable({
  providedIn: 'root',
})
export class BrokerProfileService {
  private readonly _store$ = new BehaviorSubject<any | undefined>(undefined);
  private readonly store$: Observable<any>;
  private readonly fetch$ = new Subject<undefined>();

  constructor(
    private readonly api: AgentApiService,
    private authService: AuthService,
  ) {
    this.store$ = this._store$.pipe(filter(isNonNull), distinctUntilChanged(deepEqual));
    this.authService.logout$.pipe(filter((isLoggedOut) => isLoggedOut)).subscribe(() => this.clear());

    this.fetch$
      .pipe(
        throttleTime(250),
        switchMap(() => this.authService.currentAgentSessionOrAuthError),
        switchMap((session: any) => this.api.get(session.agent.id, session.id)),
      )
      .subscribe((profile) => this._store$.next(profile));
  }

  get(): Observable<Profile> {
    if (!this._store$.getValue()) {
      this.fetch();
    }
    return this.store$;
  }

  getProperty<T>(selector: (x: Profile) => T): Observable<NonNullable<T>> {
    if (!this._store$.getValue()) {
      this.fetch();
    }
    return this.store$.pipe(map(selector), filter(isNonNull), distinctUntilChanged(deepEqual));
  }

  update(profile: Partial<Profile>): Observable<Result> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      mergeMap((session) =>
        this.api.update(session.agent.id, session.id, {
          ...this._store$.getValue(),
          ...profile,
        }),
      ),
      tap((form) => this._store$.next(form)),
      map(() => {
        return {status: ResultStatus.SUCCESS};
      }),
      catchError((err) => of({status: ResultStatus.FAIL, error: err})),
    );
  }

  updatePassword(passwordUpdateRequest: PasswordUpdateRequest): Observable<undefined> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      mergeMap((session) => this.api.updatePassword(session.id, session.agent.id, passwordUpdateRequest)),
    );
  }

  updatePasswordWithToken(passwordUpdateRequest: PasswordUpdateRequest): Observable<undefined> {
    return this.api.updatePasswordWithToken(passwordUpdateRequest);
  }

  validatePasswordToken(passwordToken: string): Observable<boolean> {
    return this.api.validatePasswordToken(passwordToken);
  }

  initiatePasswordReset(email: string): Observable<undefined> {
    return this.api.initiatePasswordReset(email);
  }

  getPublicProfile(agentId: string): Observable<PublicProfile> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      switchMap((session) => this.api.getPublicProfile(agentId, session.id)),
    );
  }

  fetch(): void {
    this.fetch$.next(undefined);
  }

  private clear(): void {
    this._store$.next(undefined);
  }
}
